<script setup lang="ts">
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import { PermissionType } from '@/enums/PermissionType';
  import { useAssistantStore } from '@/composition/assistant/use-assistant-store';
  import { useAuthStore } from '@/composition/use-auth';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  // i18n in script context (script setup has no $t)
  const proxy = getCurrentInstance()!.proxy;

  // Stores
  const assistantStore = useAssistantStore();
  const authStore = useAuthStore();
  const restaurantStore = useRestaurantStore();

  // State
  const isDismissed = ref(false);
  const hasAnimated = ref(false);

  // Computed
  /**
   * The card points at the chat bubble, which the «Asistente» panel replaces
   * for the users and stores the rollout enables (plan §9.14); there the
   * coachmark introduces the panel instead.
   */
  const shown = computed(() => !isDismissed.value && !assistantStore.enabled);

  const storageKey = computed(() => {
    const restaurantId = restaurantStore.restaurant?.id;
    return restaurantId ? `gpthelp-promo-dismissed-${restaurantId}` : null;
  });

  const hasStatsPermission = computed(() => {
    return authStore.permissionSet?.has(PermissionType.StatisticsView) ?? false;
  });

  const exampleQuestions = computed(() => {
    const statsQuestions = [
      String(proxy.$t('dashboard.aiQuestionStats1')),
      String(proxy.$t('dashboard.aiQuestionStats2')),
      String(proxy.$t('dashboard.aiQuestionStats3')),
    ];

    const menuQuestions = [
      String(proxy.$t('dashboard.aiQuestionMenu1')),
      String(proxy.$t('dashboard.aiQuestionMenu2')),
      String(proxy.$t('dashboard.aiQuestionMenu3')),
    ];

    // Mix stats and menu questions if user has permission
    if (hasStatsPermission.value) {
      return statsQuestions;
    }

    return menuQuestions;
  });

  // Repo-authored translation with inline <strong> markup, rendered v-html.
  const promoHtml = computed(() =>
    String(
      proxy.$t(
        hasStatsPermission.value
          ? 'dashboard.aiPromoTextStats'
          : 'dashboard.aiPromoTextMenu'
      )
    )
  );

  // Methods
  const checkDismissedState = () => {
    if (!storageKey.value) return;

    try {
      const dismissedData = localStorage.getItem(storageKey.value);
      if (dismissedData) {
        const parsed = JSON.parse(dismissedData);
        // Re-show after 30 days
        const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
        if (Date.now() - parsed.dismissedAt < thirtyDaysMs) {
          isDismissed.value = true;
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  };

  const dismiss = () => {
    isDismissed.value = true;

    if (!storageKey.value) return;

    try {
      localStorage.setItem(
        storageKey.value,
        JSON.stringify({ dismissedAt: Date.now() })
      );
    } catch {
      // Ignore localStorage errors
    }
  };

  const triggerAnimation = () => {
    // Small delay to ensure the component is mounted and visible
    setTimeout(() => {
      hasAnimated.value = true;
    }, 300);
  };

  // Lifecycle
  onMounted(() => {
    checkDismissedState();
    if (!isDismissed.value) {
      triggerAnimation();
    }
  });
</script>

<template>
  <v-card
    v-if="shown"
    class="gpthelp-promo-card"
    :class="{ 'animate-glow': hasAnimated }"
    elevation="0"
  >
    <v-card-title class="card-header">
      <div class="header-content">
        <div class="header-left">
          <v-icon class="header-icon">mdi-robot-happy</v-icon>
          <span class="header-title">{{ $t('dashboard.aiTitle') }}</span>
          <span class="badge-new">{{ $t('dashboard.aiBadgeNew') }}</span>
        </div>
        <v-btn
          icon
          small
          class="close-btn"
          @click="dismiss"
          :aria-label="String($t('close'))"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text class="card-content">
      <p class="promo-text" v-html="promoHtml"></p>

      <div class="examples-section">
        <span class="examples-label">
          <v-icon x-small class="label-icon">mdi-lightbulb-on</v-icon>
          {{ $t('dashboard.aiTryAsking') }}
        </span>
        <ul class="examples-list">
          <li v-for="question in exampleQuestions" :key="question">
            <v-icon x-small class="bullet-icon">mdi-chat-question</v-icon>
            <span class="question-text">"{{ question }}"</span>
          </li>
        </ul>
      </div>

      <div class="cta-section">
        <div class="cta-content">
          <v-icon class="cta-icon">mdi-arrow-bottom-left</v-icon>
          <i18n path="dashboard.aiCta" tag="span" class="cta-text">
            <template #bubble>
              <span class="bubble-highlight">
                <v-icon x-small>mdi-chat</v-icon>
                {{ $t('dashboard.aiCtaBubble') }}
              </span>
            </template>
          </i18n>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .gpthelp-promo-card {
    @apply tw-bg-white tw-rounded-lg tw-shadow-sm tw-overflow-hidden;
    @apply tw-border tw-border-neutral-100;
    @apply tw-relative;

    // Left accent border
    &::before {
      content: '';
      @apply tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-bg-orange-500;
      @apply tw-w-1;
      //background: linear-gradient(180deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
    }

    // Glow animation
    &.animate-glow {
      animation: glow-pulse 2s ease-in-out 2;
    }

    .card-header {
      @apply tw-pb-1 tw-pt-3 tw-pl-4;

      .header-content {
        @apply tw-flex tw-items-center tw-justify-between tw-w-full;

        .header-left {
          @apply tw-flex tw-items-center tw-gap-2;

          .header-icon {
            @apply tw-text-orange-500;
            font-size: 20px;
          }

          .header-title {
            @apply tw-text-base tw-font-semibold tw-text-neutral-800;
          }

          .badge-new {
            @apply tw-text-xs tw-font-medium tw-px-2 tw-py-0.5;
            @apply tw-bg-gradient-to-r tw-from-orange-500 tw-to-amber-500;
            @apply tw-text-white tw-rounded-full;
            @apply tw-uppercase tw-tracking-wide;
            font-size: 10px;
          }
        }

        .close-btn {
          @apply tw-text-neutral-400;

          &:hover {
            @apply tw-text-neutral-600 tw-bg-neutral-100;
          }
        }
      }
    }

    .card-content {
      @apply tw-pt-2 tw-pl-4;

      .promo-text {
        @apply tw-text-sm tw-text-neutral-600 tw-mb-3 tw-leading-relaxed;

        // v-html content never gets the scope attribute; ::v-deep is required.
        ::v-deep strong {
          @apply tw-text-neutral-700 tw-font-medium;
        }
      }

      .examples-section {
        @apply tw-mb-3;

        .examples-label {
          @apply tw-text-xs tw-text-neutral-500 tw-font-medium tw-flex tw-items-center tw-gap-1 tw-mb-2;

          .label-icon {
            @apply tw-text-amber-500;
          }
        }

        .examples-list {
          @apply tw-list-none tw-p-0 tw-m-0 tw-space-y-1.5;

          li {
            @apply tw-flex tw-items-start tw-gap-2;
            @apply tw-text-sm tw-text-neutral-700;
            @apply tw-p-2 tw-rounded-md;
            @apply tw-bg-gradient-to-r tw-from-orange-50 tw-to-amber-50;
            @apply tw-border tw-border-orange-100;
            @apply tw-transition-all tw-duration-200;

            &:hover {
              @apply tw-from-orange-100 tw-to-amber-100;
              @apply tw-border-orange-200;
              transform: translateX(2px);
            }

            .bullet-icon {
              @apply tw-text-orange-500 tw-mt-0.5 tw-flex-shrink-0;
            }

            .question-text {
              @apply tw-font-medium tw-italic;
            }
          }
        }
      }

      .cta-section {
        @apply tw-pt-2 tw-border-t tw-border-neutral-100;

        .cta-content {
          @apply tw-flex tw-items-center tw-gap-2;

          .cta-icon {
            @apply tw-text-orange-500;
            font-size: 20px;
            animation: bounce-diagonal 1.5s ease-in-out infinite;
          }

          .cta-text {
            @apply tw-text-sm tw-text-neutral-600;

            .bubble-highlight {
              @apply tw-inline-flex tw-items-center tw-gap-1;
              @apply tw-bg-orange-100 tw-text-orange-700;
              @apply tw-px-2 tw-py-0.5 tw-rounded-full;
              @apply tw-font-medium;

              .v-icon {
                @apply tw-text-orange-500;
              }
            }
          }
        }
      }
    }
  }

  // Animations
  @keyframes glow-pulse {
    0%,
    100% {
      box-shadow: 0 0 0 0 rgba(249, 115, 22, 0);
    }
    50% {
      box-shadow: 0 0 20px 4px rgba(249, 115, 22, 0.3);
    }
  }

  @keyframes bounce-diagonal {
    0%,
    100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(-3px, 3px);
    }
  }

  // Responsive adjustments
  @media (max-width: 640px) {
    .gpthelp-promo-card {
      .card-content {
        .examples-section {
          .examples-list {
            li {
              @apply tw-p-1.5;
              @apply tw-text-xs;
            }
          }
        }
      }
    }
  }
</style>
